import {
  Button,
  CondensedButton,
  Flex,
  Grid,
  Heading,
  IconAddFolderLine,
  IconArrowOpenDownLine,
  IconArrowOpenEndLine,
  IconButton,
  IconFolderLine,
  IconOpenFolderLine,
  IconSubaccountsLine,
  IconTrashLine,
  InPlaceEdit,
  Popover,
  Spinner,
  Text,
  ToggleGroup,
  Tooltip,
  View,
} from "@instructure/ui";
import { t } from "i18next";
import { useContext, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { AcademicHierarchyService } from "../../services/AcademicHierarchyService";
import { UserDetailContext } from "../../utils/Context";
import { sort } from "../../utils/utils";
import { ERROR, useNotification } from "../CustomHooks/NotificationProvider";
import { DialogYesNo } from "../DialogYesNo/DialogYesNo";
import { MULTI, Selector } from "../Selectors/Selector";
import Department from "./Department";
import { DepartmentSection } from "./DepartmentSection";
import { SubjectContext } from "./OrganizationalStructure";

let selectedSubjects = [];

const SubLevel = ({ parent, setParent, level, sub, hierarchy }) => {
  const showNotification = useNotification();
  const userDetails = useContext(UserDetailContext);
  const { subjects, setSubjects } = useContext(SubjectContext);
  const [levelData, setLevelData] = useState(level);
  const [nameEditorMode, setNameEditorMode] = useState("view");
  const [removeLevelDialogOpen, setRemoveLevelDialogOpen] = useState(false);
  const [levelToRemove, setLevelToRemove] = useState({});
  const [showingDepartment, setShowingDepartment] = useState(false);
  const [openAddDepartment, setOpenAddDepartment] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingAddDepartment, setLoadingAddDepartment] = useState(false);
  const [loadingNewLevel, setLoadingNewLevel] = useState(false);
  const [levelExpanded, setLevelExpanded] = useState(false);

  // Renders the view component
  const renderView = (value, weight, message) => (
    <Text
      lineHeight="double"
      color={value ? "primary" : "secondary"}
      weight={value ? weight : "light"}
      size={value ? "medium" : "large"}
    >
      {value || message}
    </Text>
  );

  // Renders the edit button.
  // Leverage the default implementation provided by InPlaceEdit
  const renderEditButton = (props, message) => {
    props.label = message;
    return InPlaceEdit.renderDefaultEditButton(props);
  };

  const sortByLabel = (array) => {
    return sort(array, (item) => item.label);
  };

  const labeledSubject = (subject) => {
    return {
      id: subject.id,
      title: subject.title,
      name: subject.name,
      label: subject.name,
      creation_method: subject.creation_method,
    };
  };

  const sortByLabelAndCreationMethod = (array) => {
    return array.sort((a, b) => {
      if (
        a.label === AcademicHierarchyService.IS_ACADEMIC_SUBJECT &&
        b.label !== AcademicHierarchyService.IS_ACADEMIC_SUBJECT
      ) {
        return 1;
      } else if (
        a.label !== AcademicHierarchyService.IS_ACADEMIC_SUBJECT &&
        b.label === AcademicHierarchyService.IS_ACADEMIC_SUBJECT
      ) {
        return -1;
      }
      return a.name.localeCompare(b.name);
    });
  };

  const handleDepartment = (selected) => {
    setLoadingAddDepartment(true);
    if (!levelData.subArray) {
      AcademicHierarchyService.getLevels(levelData.ddl_id)
        .then((levels) => {
          const newLevelData = { ...levelData, subArray: levels };
          setLevelData(newLevelData);
          handleAddDepartment(selected, newLevelData);
        })
        .catch(() =>
          showNotification(
            t("notifications.general.addError", {
              obj: t("academicHierarchy.department"),
            }),
            ERROR
          )
        );
    } else {
      handleAddDepartment(selected, levelData);
    }
  };

  const updateDepartmentList = (newDepartment) => {
    const filteredSubjects = subjects.filter(
      (sub) => sub.id !== newDepartment.id
    );
    filteredSubjects.push(labeledSubject(newDepartment));
    setSubjects(sortByLabel(filteredSubjects));
  };

  const onEditDepartmentCallBack = (updatedDepartment) => {
    updateDepartmentList(updatedDepartment);
    updatedDepartment.ddl_id = updatedDepartment.id;
    delete updatedDepartment.id;
    updatedDepartment.label = AcademicHierarchyService.IS_ACADEMIC_SUBJECT;
    const filteredSubArray = levelData.subArray.filter(
      (sub) => sub.ddl_id !== updatedDepartment.ddl_id
    );
    filteredSubArray.push(updatedDepartment);
    setLevelData((prevData) => ({
      ...prevData,
      subArray: sortByLabelAndCreationMethod(filteredSubArray),
    }));
  };

  const handleAddDepartment = (selectedSubjects, data) => {
    const existingLevel = JSON.parse(JSON.stringify(data));

    selectedSubjects.forEach((item) => {
      // Check if subjects is already attached to this level
      let found = false;
      for (let i = 0; i < existingLevel.subArray.length; i++) {
        if (
          existingLevel.subArray[i].label ===
            AcademicHierarchyService.IS_ACADEMIC_SUBJECT &&
          existingLevel.subArray[i].ddl_id === item
        ) {
          found = true;
          break;
        }
      }
      if (found) {
        return;
      }

      const subjectId = item;
      let subjectTitle = "";
      let subjectName = "";
      subjects.forEach((ele) => {
        if (ele.id === subjectId) {
          subjectTitle = ele.title;
          subjectName = ele.name;
        }
      });

      const newData = {
        ddl_id: subjectId,
        name: subjectName,
        title: subjectTitle,
        label: AcademicHierarchyService.IS_ACADEMIC_SUBJECT,
      };

      existingLevel.subArray.splice(0, 0, newData);
    });

    const newSubjectList = [];
    existingLevel.subArray.forEach((subject) => {
      if (subject.label === AcademicHierarchyService.IS_ACADEMIC_SUBJECT) {
        newSubjectList.push(subject.ddl_id);
      }
    });
    AcademicHierarchyService.setSubjects(existingLevel, newSubjectList)
      .then(() => {
        handleOnToggle(data.ddl_id, true);
        setShowingDepartment(false);
        setLoadingAddDepartment(false);
        showNotification(
          t("notifications.general.addSuccess", {
            obj: t("academicHierarchy.department"),
          })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.addError", {
            obj: t("academicHierarchy.department"),
          }),
          ERROR
        )
      );
  };

  const showRemoveLevelConfirmation = (levelToRemove) => {
    setLevelToRemove(levelToRemove);
    setRemoveLevelDialogOpen(true);
  };

  const handleRemoveLevel = async () => {
    await AcademicHierarchyService.deleteLevel(levelToRemove.ddl_id)
      .then(() => {
        const levels = parent ? parent.subArray : hierarchy;
        const index = levels.findIndex(
          (lvl) => lvl.ddl_id === levelToRemove.ddl_id
        );
        if (index > -1) {
          levels.splice(index, 1);
        }
        if (parent) {
          setParent((prevData) => ({ ...prevData, subArray: levels }));
        }
        setRemoveLevelDialogOpen(false);
        setLevelToRemove({});

        showNotification(
          t("notifications.general.deleteSuccess", {
            obj: t("academicHierarchy.hierarchyLevel"),
          })
        );
      })
      .catch(() => {
        showNotification(
          t("notifications.general.deleteError", {
            obj: t("academicHierarchy.hierarchyLevel"),
          }),
          ERROR
        );
      });
  };

  const handleAddLevel = () => {
    setLoadingNewLevel(true);
    const newData = {
      parentLevelId: levelData.ddl_id,
      name: t("academicHierarchy.newLevel"),
    };

    AcademicHierarchyService.createLevel(newData.parentLevelId, newData.name)
      .then(() => {
        handleOnToggle(levelData.ddl_id, true);
        setLoadingNewLevel(false);
        showNotification(
          t("notifications.general.addSuccess", {
            obj: t("academicHierarchy.hierarchyLevel"),
          })
        );
      })
      .catch(() =>
        showNotification(
          t("notifications.general.addError", {
            obj: t("academicHierarchy.hierarchyLevel"),
          }),
          ERROR
        )
      );
  };

  const fetchDetailsSubject = (levels, subjects) => {
    // Enrich levels with all details from subject (id, title and name)
    for (let i = 0; i < levels.length; i++) {
      const extSubjects = [];
      if (levels[i].subjects) {
        const levSubjects = levels[i].subjects;
        levSubjects.forEach((levItem) => {
          subjects.forEach((item) => {
            if (item.id === levItem) {
              extSubjects.push(item);
            }
          });
        });
        levels[i].subjects = extSubjects;
      }
    }
    return levels;
  };

  const handleOnToggle = (id, expanded) => {
    setLevelExpanded(expanded);
    if (expanded) {
      setLoading(true);
      AcademicHierarchyService.getLevels(id)
        .then((levels) => {
          levels = fetchDetailsSubject(levels, subjects);
          setLevelData((prevData) => ({
            ...prevData,
            loaded: true,
            subArray: sortByLabelAndCreationMethod(levels),
          }));
          setLoading(false);
        })
        .catch(() =>
          showNotification(
            t("notifications.general.fetchError", {
              obj: t("academicHierarchy.hierarchyLevels"),
            }),
            ERROR
          )
        );
    }
  };

  const saveLevelName = (level, newName) => {
    if (newName !== level.name) {
      setLevelData((prevData) => ({ ...prevData, name: newName }));
      AcademicHierarchyService.setLevelTitle(level.ddl_id, newName);
    }
  };

  const handleLevelNameBlur = (event, onB) => {
    if (event.nativeEvent.sourceCapabilities != null) {
      onB(event);
    }
  };

  const renderEdit = (level, props) => {
    return (
      <Text
        color="primary"
        size="medium"
        as="input"
        type="text"
        defaultValue={level.name}
        aria-label={t("academicHierarchy.newLevel")}
        onBlur={(event) => {
          handleLevelNameBlur(event, props.onBlur);
          saveLevelName(levelData, event.target.value);
        }}
        elementRef={props.editorRef}
      />
    );
  };

  return (
    <>
      <ToggleGroup
        size="small"
        border={!sub}
        toggleLabel={t("academicHierarchy.toggleLevel")}
        iconExpanded={IconOpenFolderLine}
        icon={IconFolderLine}
        expanded={levelExpanded}
        onToggle={(_e, expanded) => handleOnToggle(levelData.ddl_id, expanded)}
        summary={
          <Grid vAlign="middle">
            <Grid.Row>
              <Grid.Col>
                <InPlaceEdit
                  renderViewer={() =>
                    renderView(
                      levelData.name,
                      "normal",
                      t("academicHierarchy.editLevelName")
                    )
                  }
                  renderEditor={(props) => renderEdit(levelData, props)}
                  renderEditButton={(props) =>
                    renderEditButton(
                      props,
                      t("academicHierarchy.editLevelName")
                    )
                  }
                  onChangeMode={(mode) => setNameEditorMode(mode)}
                  mode={nameEditorMode}
                  value={levelData.name}
                  inline={true}
                  readOnly={!userDetails.isAC()}
                />
              </Grid.Col>
              {userDetails.isAC() && (
                <Grid.Col width="auto">
                  <Popover
                    id={`add-popover-${levelData.ddl_id}`}
                    placement="start top"
                    renderTrigger={
                      <Tooltip
                        color="primary-inverse"
                        renderTip={t("academicHierarchy.addAcDepartment")}
                      >
                        <IconButton
                          id={`add-department-${levelData.ddl_id}`}
                          renderIcon={IconSubaccountsLine}
                          screenReaderLabel={t(
                            "academicHierarchy.addAcDepartment"
                          )}
                        />
                      </Tooltip>
                    }
                    isShowingContent={showingDepartment}
                    onShowContent={() => setShowingDepartment(true)}
                    onHideContent={() => setShowingDepartment(false)}
                    on="click"
                    screenReaderLabel={t(
                      "academicHierarchy.addAcDepartmentPopover"
                    )}
                    mountNode={() => document.getElementById("main")}
                  >
                    <View padding="small" display="block" width={500}>
                      <Flex
                        justifyItems="space-between"
                        alignItems="start"
                        margin="0 0 small 0"
                      >
                        <Flex.Item>
                          <Heading as="h3" level="h4">
                            <Text
                              weight="bold"
                              size="small"
                              htmlFor="department-select"
                            >
                              {t("academicHierarchy.department")}
                            </Text>
                          </Heading>
                        </Flex.Item>
                        {userDetails.isAC() && (
                          <Flex.Item>
                            <CondensedButton
                              size="small"
                              onClick={() =>
                                setOpenAddDepartment((prevOpen) => !prevOpen)
                              }
                            >
                              {openAddDepartment ? (
                                <IconArrowOpenDownLine />
                              ) : (
                                <IconArrowOpenEndLine />
                              )}{" "}
                              {t("academicHierarchy.createDepartment")}
                            </CondensedButton>
                          </Flex.Item>
                        )}
                      </Flex>
                      {openAddDepartment ? (
                        <DepartmentSection
                          setOpenSection={setOpenAddDepartment}
                          upsertHandler={updateDepartmentList}
                        />
                      ) : (
                        <Flex gap="small" wrap="wrap">
                          <Flex.Item width="100%">
                            <Selector
                              type={MULTI}
                              id="department-select"
                              size="small"
                              label={t("academicHierarchy.selectDepartments")}
                              options={
                                userDetails.isAC()
                                  ? subjects
                                  : subjects.filter((sub) =>
                                      userDetails.ac_subjects.includes(
                                        sub.title
                                      )
                                    )
                              }
                              selectHandler={(selectedOptionId) =>
                                (selectedSubjects = selectedOptionId)
                              }
                            />
                          </Flex.Item>
                          <Flex.Item shouldGrow textAlign="end">
                            <Button
                              size="small"
                              color="primary"
                              onClick={() => handleDepartment(selectedSubjects)}
                              disabled={loadingAddDepartment}
                            >
                              {loadingAddDepartment ? (
                                <Spinner
                                  renderTitle={t("global.pleaseWait")}
                                  size="x-small"
                                />
                              ) : (
                                t("academicHierarchy.addAcDepartment")
                              )}
                            </Button>
                          </Flex.Item>
                        </Flex>
                      )}
                    </View>
                  </Popover>
                  <Tooltip
                    color="primary-inverse"
                    renderTip={t("academicHierarchy.addLevel")}
                  >
                    <IconButton
                      id={`add-level-${levelData.ddl_id}`}
                      renderIcon={
                        loadingNewLevel ? (
                          <Spinner
                            renderTitle={t("academicHierarchy.addingLevel")}
                            size="x-small"
                          />
                        ) : (
                          IconAddFolderLine
                        )
                      }
                      onClick={handleAddLevel}
                      screenReaderLabel={t("academicHierarchy.addLevel")}
                      margin="0 0 0 small"
                      disabled={loadingNewLevel}
                    />
                  </Tooltip>
                  <Tooltip
                    color="primary-inverse"
                    renderTip={t("academicHierarchy.removeLevel")}
                  >
                    <IconButton
                      id={`remove-level-${levelData.ddl_id}`}
                      renderIcon={IconTrashLine}
                      onClick={() => showRemoveLevelConfirmation(levelData)}
                      screenReaderLabel={t("academicHierarchy.removeLevel")}
                      margin="0 0 0 small"
                    />
                  </Tooltip>
                </Grid.Col>
              )}
            </Grid.Row>
          </Grid>
        }
      >
        <View>
          <>
            {(!levelData.subArray || levelData.subArray.length === 0) &&
              (loading ? (
                <Skeleton style={{ height: 64, borderRadius: 5 }} />
              ) : (
                <div style={{ marginLeft: 25, padding: 10 }}>
                  {t("academicHierarchy.noContent")}
                </div>
              ))}
            {levelData.subArray &&
              levelData.subArray.map(
                (lvl) =>
                  (lvl.label === "hierarchy level" ||
                    lvl.label ===
                      AcademicHierarchyService.IS_ACADEMIC_SUBJECT) && (
                    <div
                      key={lvl.ddl_id + "-holder"}
                      style={{ paddingLeft: 12 }}
                    >
                      {lvl.label ===
                      AcademicHierarchyService.IS_ACADEMIC_SUBJECT ? (
                        <Department
                          key={lvl.ddl_id}
                          department={lvl}
                          parent={levelData}
                          onEditDepartmentCallback={onEditDepartmentCallBack}
                        />
                      ) : (
                        lvl.label === "hierarchy level" && (
                          <SubLevel
                            key={lvl.ddl_id}
                            parent={levelData}
                            setParent={setLevelData}
                            level={lvl}
                            sub={true}
                            hierarchy={hierarchy}
                          />
                        )
                      )}
                    </div>
                  )
              )}
          </>
        </View>
      </ToggleGroup>
      <DialogYesNo
        message={t("academicHierarchy.deleteLevelMessage")}
        openStatus={removeLevelDialogOpen}
        onClickNo={() => setRemoveLevelDialogOpen(false)}
        onClickYes={handleRemoveLevel}
      />
    </>
  );
};

export default SubLevel;
